import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import TrendingSlider from "@/components/TrendingSlider";
import EventCard from "@/components/EventCard";
import GenieBar from "@/components/GenieBar";
import FanJourney from "@/components/FanJourney";
import StatsRibbon from "@/components/StatsRibbon";
import HowItWorks from "@/components/HowItWorks";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import MatchSnippetsTicker from "@/components/MatchSnippetsTicker";
import LatestEventsSlider from "@/components/LatestEventsSlider";
import FeatureTiles from "@/components/FeatureTiles";
import OnboardingInviteNudge from "@/components/OnboardingInviteNudge";
import { useCity } from "@/context/CityContext";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, Sparkles, Users, TrendingUp, Zap, Mail, ScanLine, Music, Flame, Star, Calendar, Utensils, Palette, Trophy, Cpu, Wrench, Plane, Coffee, MoreHorizontal, Crown, Building2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const categories = ["All", "College Fest", "Music Festival", "Corporate Event", "Technical Festival", "Cultural", "Fashion Show", "Wedding", "Open Mic", "VIP After Party", "House Party"];

const HOMEPAGE_CATEGORIES = [
  { key: "Concerts", icon: Music, color: "#AAEE00" },
  { key: "Nightlife", icon: Flame, color: "#FF4D6D" },
  { key: "Comedy", icon: Sparkles, color: "#00D4FF" },
  { key: "Food and Drink", icon: Utensils, color: "#AAEE00" },
  { key: "Art and Culture", icon: Palette, color: "#7B2FFF" },
  { key: "Sports", icon: Trophy, color: "#FF4D6D" },
  { key: "Tech", icon: Cpu, color: "#00D4FF" },
  { key: "Workshop", icon: Wrench, color: "#AAEE00" },
  { key: "Trips", icon: Plane, color: "#7B2FFF" },
  { key: "Meetups", icon: Coffee, color: "#FF4D6D" },
  { key: "VIP After Party", icon: Crown, color: "#7B2FFF" },
  { key: "Other", icon: MoreHorizontal, color: "#B4B4C0" },
];

const HERO_WORDS = [
  { w: "Concerts.", c: "#AAEE00" },
  { w: "Comedy.", c: "#FF007F" },
  { w: "Nightlife.", c: "#00D4FF" },
  { w: "College fests.", c: "#7B2FFF" },
  { w: "Anything.", c: "#FF6A00" },
];

function LeaderboardTeaser() {
  const [top, setTop] = useState([]);
  useEffect(() => {
    api.get("/referrals/leaderboard?limit=3").then(({ data }) => setTop(data.leaderboard || [])).catch(() => {});
  }, []);
  return (
    <div className="card-lime p-6 md:p-7 flex flex-wrap items-center justify-between gap-5">
      <div className="min-w-0">
        <div className="chip !border-lime/50 !text-lime mb-2"><Trophy className="w-3 h-3" /> Squad Leaderboard</div>
        <div className="font-display text-2xl md:text-3xl leading-tight text-white">Bring your squad. <span className="text-lime">Top the city.</span></div>
        <div className="text-xs text-white/60 mt-1">₹100 off for every friend — and a spot on the board for you.</div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {top.length > 0 && (
          <div className="flex items-center gap-3">
            {top.map((e, i) => (
              <div key={e.user_id} className="text-center" data-testid={`teaser-top-${i + 1}`}>
                <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: ["#F5D061", "#B4B4C0", "#FF4D6D"][i] }}>#{e.rank}</div>
                <div className="font-heading font-bold text-xs truncate max-w-[80px]">{e.name}</div>
                <div className="font-mono text-lime text-sm font-bold">{e.conversions}</div>
              </div>
            ))}
          </div>
        )}
        <Link to="/leaderboard" data-testid="teaser-leaderboard-cta" className="btn-primary !py-2.5 !px-6 !text-sm">See The Board <ArrowUpRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

function Hero({ events }) {
  const [wi, setWi] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWi((i) => (i + 1) % HERO_WORDS.length), 3000);
    return () => clearInterval(t);
  }, []);
  const word = HERO_WORDS[wi];
  // Pull first 4 UPCOMING events with a banner for the bento grid
  const isUpcoming = (e) => {
    if (!e?.date) return true;
    try {
      const d = e.end_date || e.date;
      const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
      return dt >= new Date();
    } catch { return true; }
  };
  const bento = (events || [])
    .filter((e) => e.banner && isUpcoming(e))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 4);

  return (
    <section className="relative overflow-hidden poster-canvas" data-testid="home-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-20 relative">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* LEFT — poster headline */}
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-lime/40">
              <span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse" />
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-lime">Live · India</span>
            </div>

            <h1 className="mt-6 font-fancy leading-[0.88] tracking-tight text-white uppercase" style={{ fontSize: "clamp(40px, 6.5vw, 76px)" }}>
              Book the night.<br/>
              <span className="marker-lime">Meet the crowd.</span><br/>
              Live the vibe.
            </h1>

            <p className="mt-6 text-lg text-white/70 max-w-md leading-relaxed">
              India's real live-culture app. Events, tickets, parties, people — one tap.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#events-grid" data-testid="cta-explore-events" className="btn-poster">Explore Events →</a>
              <Link to="/clubvip" data-testid="cta-match" className="text-white/85 text-sm font-heading font-bold underline underline-offset-4 hover:text-lime">
                Join the Inner Circle →
              </Link>
            </div>

            <div className="mt-10 signature-script text-2xl md:text-3xl">More than events.</div>
          </div>

          {/* RIGHT — hero photograph, VIP poster style */}
          <div className="lg:col-span-6">
            {bento.length >= 1 ? (
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px] lg:h-[620px]" data-testid="hero-bento">
                <Link to={bento[0].slug ? `/e/${bento[0].slug}` : `/events/${bento[0].id}`} data-testid="hero-bento-0"
                  className="col-span-4 row-span-6 relative rounded-3xl overflow-hidden group">
                  <img src={bento[0].banner} alt={bento[0].title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/95 via-black/50 to-transparent">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-1">{bento[0].city || "India"} · {new Date(bento[0].date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                    <div className="font-display uppercase text-2xl lg:text-3xl leading-tight text-white line-clamp-2">{bento[0].title}</div>
                  </div>
                  <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-lime text-ink text-[9px] font-mono uppercase tracking-widest font-bold">Live now</div>
                </Link>
                <div className="col-span-2 row-span-6 flex flex-col gap-3">
                  <img src="/assets/festival-girls.webp" alt="Real Eventom crowd" className="rounded-3xl object-cover w-full h-1/2" data-testid="hero-photo-crowd" />
                  <img src="/assets/vip-portrait.webp" alt="Club VIP" className="rounded-3xl object-cover w-full h-1/2" data-testid="hero-photo-vip" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 h-[520px]">
                <img src="/assets/festival-girls.webp" alt="Eventom fans" className="rounded-3xl object-cover w-full h-full" />
                <img src="/assets/vip-portrait.webp" alt="Eventom VIP" className="rounded-3xl object-cover w-full h-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function TakeoverStrip({ cfg }) {
  const headline = cfg?.headline ?? "Girls";
  const tail = cfg?.tail ?? "Takeover.";
  const eyebrow = cfg?.eyebrow ?? "● This week";
  const meta = cfg?.meta_line ?? "Thursday · 10 PM · Patna";
  const subcopy = cfg?.subcopy ?? "Open decks, free entry till 11, two queens on the console, and a dance floor that doesn't quit.";
  const ctaLabel = cfg?.cta_label ?? "RSVP for Free →";
  const ctaHref = cfg?.cta_href ?? "/events";
  const secLabel = cfg?.secondary_label ?? "Stuck? WhatsApp us";
  const secHref = cfg?.secondary_href ?? "https://wa.me/917250120666?text=Hi%2C%20tell%20me%20more%20about%20Girls%20Takeover";
  const img = cfg?.image_url ?? "/assets/vip-portrait.webp";
  const badge = cfg?.sticker_badge ?? "₹0";
  const badgeSub = cfg?.sticker_sub ?? "entry";
  return (
    <section className="relative themed-pink sticker-wall py-14 md:py-20 overflow-hidden" data-testid="takeover-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/15 backdrop-blur border border-white/30 mb-4">
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-white">{eyebrow}</span>
          </div>
          <h2 className="font-fancy uppercase leading-[0.86] text-white" style={{ fontSize: "clamp(44px, 8vw, 110px)", textShadow: "4px 4px 0 rgba(0,0,0,0.25)" }}>
            {headline}<br/>
            <span className="text-striped" style={{ fontSize: "1.05em" }}>{tail}</span>
          </h2>
          <div className="mt-5 flex items-center gap-3 text-white/95">
            <span className="w-10 h-[2px] bg-white" />
            <span className="font-mono text-[11px] uppercase tracking-widest">{meta}</span>
          </div>
          <p className="mt-4 text-white/90 text-base max-w-md leading-relaxed">{subcopy}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to={ctaHref} className="btn-pill-white" data-testid="takeover-rsvp">{ctaLabel}</Link>
            {secLabel && <a href={secHref} target={secHref?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="btn-pill-dark">{secLabel}</a>}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] relative shadow-2xl rotate-[-3deg]">
            {img && <img src={img} alt="" className="w-full h-full object-cover" />}
            <div className="absolute top-4 left-4 bg-lime text-ink px-3 py-1 rounded-full font-mono font-bold text-[10px] uppercase tracking-widest">Live DJ</div>
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-90">Headliner</div>
              <div className="font-fancy text-3xl md:text-4xl leading-none">DJ Inexa.</div>
            </div>
          </div>
          <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-lime text-ink flex items-center justify-center rotate-12 shadow-xl">
            <div className="text-center">
              <div className="font-fancy text-xl leading-none">{badge}</div>
              <div className="font-mono text-[8px] uppercase tracking-widest">{badgeSub}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RizzStrip({ cfg }) {
  const headline = cfg?.headline ?? "48";
  const tail = cfg?.tail ?? "Hours.";
  const eyebrow = cfg?.eyebrow ?? "● Right now";
  const meta = cfg?.meta_line ?? "Flash drops · Weekend only · City first";
  const subcopy = cfg?.subcopy ?? "Pop-ups, after-hours tables, surprise sets — booked within 48 hours or they're gone. Fastest fingers, best stories.";
  const ctaLabel = cfg?.cta_label ?? "See the Drops →";
  const ctaHref = cfg?.cta_href ?? "/events";
  const secLabel = cfg?.secondary_label ?? "Members get first dibs";
  const secHref = cfg?.secondary_href ?? "/clubvip";
  const img = cfg?.image_url ?? "/assets/festival-girls.webp";
  const badge = cfg?.sticker_badge ?? "48";
  const badgeSub = cfg?.sticker_sub ?? "hours only";
  return (
    <section className="relative themed-blue sticker-wall py-14 md:py-20 overflow-hidden" data-testid="rizz-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1 relative">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] relative shadow-2xl rotate-[3deg] border-4 border-white">
            {img && <img src={img} alt="" className="w-full h-full object-cover" />}
            <div className="absolute top-4 left-4 bg-white text-[#0955F2] px-3 py-1 rounded-full font-mono font-bold text-[10px] uppercase tracking-widest">City Rizz</div>
            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="font-mono text-[10px] uppercase tracking-widest opacity-90">This weekend</div>
              <div className="font-fancy text-3xl md:text-4xl leading-none">Pop-Up Rooftop.</div>
            </div>
          </div>
          <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-black text-lime flex items-center justify-center -rotate-6 shadow-xl">
            <div className="text-center">
              <div className="font-fancy text-2xl leading-none">{badge}</div>
              <div className="font-mono text-[8px] uppercase tracking-widest">{badgeSub}</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/20 backdrop-blur border border-white/30 mb-4">
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-white">{eyebrow}</span>
          </div>
          <h2 className="font-fancy uppercase leading-[0.86] text-white" style={{ fontSize: "clamp(44px, 8vw, 110px)", textShadow: "4px 4px 0 rgba(0,0,0,0.3)" }}>
            {headline}<br/>
            <span className="text-striped" style={{ fontSize: "1.05em" }}>{tail}</span>
          </h2>
          <div className="mt-5 flex items-center gap-3 text-white/95">
            <span className="w-10 h-[2px] bg-white" />
            <span className="font-mono text-[11px] uppercase tracking-widest">{meta}</span>
          </div>
          <p className="mt-4 text-white/90 text-base max-w-md leading-relaxed">{subcopy}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to={ctaHref} className="btn-pill-white" data-testid="rizz-cta">{ctaLabel}</Link>
            {secLabel && <Link to={secHref} className="btn-pill-dark" data-testid="rizz-vip-cta">{secLabel}</Link>}
          </div>
        </div>
      </div>
    </section>
  );
}

function DJFlyerStrip({ cfg }) {
  const headline = cfg?.headline ?? "Disco";
  const tail = cfg?.tail ?? "Fever.";
  const eyebrow = cfg?.eyebrow ?? "● Tonight · 10 PM";
  const meta = cfg?.meta_line ?? "Live DJ · 4 cities · Zero cover";
  const subcopy = cfg?.subcopy ?? "Four DJs. Four rooftops. One kaleidoscopic night. Mirror balls, zero cover charge, and the loudest sunrise in town.";
  const ctaLabel = cfg?.cta_label ?? "Grab the Guest List →";
  const ctaHref = cfg?.cta_href ?? "/events";
  const secLabel = cfg?.secondary_label ?? "WhatsApp us ↗";
  const secHref = cfg?.secondary_href ?? "https://wa.me/917250120666?text=Disco%20Fever%20guest%20list";
  const badge = cfg?.sticker_badge ?? "4";
  const badgeSub = cfg?.sticker_sub ?? "cities";
  return (
    <section className="relative themed-yellow sticker-wall py-14 md:py-18 overflow-hidden" data-testid="dj-flyer-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black text-[#FFC600] border border-black mb-4">
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase font-bold">{eyebrow}</span>
          </div>
          <h2 className="font-fancy uppercase leading-[0.84] text-black" style={{ fontSize: "clamp(48px, 9vw, 128px)", textShadow: "5px 5px 0 rgba(255,255,255,0.4)" }}>
            {headline}<br/>
            <span className="text-striped" style={{ fontSize: "1.05em", color: "#0A0A0F" }}>{tail}</span>
          </h2>
          <div className="mt-5 flex items-center gap-3 text-black">
            <span className="w-10 h-[2px] bg-black" />
            <span className="font-mono text-[11px] uppercase tracking-widest font-bold">{meta}</span>
          </div>
          <p className="mt-4 text-black/85 text-base max-w-md leading-relaxed font-medium">{subcopy}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to={ctaHref} className="btn-pill-dark !text-[12px] !py-3 !px-6" data-testid="dj-flyer-cta">{ctaLabel}</Link>
            {secLabel && <a href={secHref} target={secHref?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="font-mono text-[11px] uppercase tracking-widest text-black underline underline-offset-4 font-bold">{secLabel}</a>}
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-square">
            <div className="absolute inset-6 rounded-full bg-gradient-to-br from-white via-[#E8E8E8] to-[#A8A8A8] shadow-2xl" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, #FFFFFF, #B0B0B0 60%, #606060 100%)" }} />
            <div className="absolute inset-6 rounded-full opacity-70" style={{ backgroundImage: "repeating-conic-gradient(from 0deg, rgba(255,255,255,0.6) 0deg 8deg, rgba(0,0,0,0.1) 8deg 16deg)" }} />
            <div className="absolute -top-2 -right-2 w-20 h-20 rounded-full bg-black text-[#FFC600] flex items-center justify-center rotate-12 shadow-xl">
              <div className="text-center">
                <div className="font-fancy text-xl leading-none">{badge}</div>
                <div className="font-mono text-[8px] uppercase tracking-widest font-bold">{badgeSub}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AudienceCallouts() {
  const rows = [
    {
      audience: "For Organisers",
      title: "Find sponsors, sell out tickets.",
      pitch: "One dashboard. Ticketing, gate scans, sponsorship inbox, boosts.",
      href: "/organizer",
      cta: "Get your event live",
      img: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&auto=format&fit=crop&q=70",
      accent: "lime",
      testId: "callout-organiser",
    },
    {
      audience: "For Ecosystem",
      title: "Find your next gig.",
      pitch: "Artists, photographers, venues, decor — get inbound work from real events.",
      href: "/organisers",
      cta: "Grow Your Business",
      img: "/assets/festival-girls.webp",
      accent: "skyx",
      testId: "callout-ecosystem",
    },
    {
      audience: "For You",
      title: "Join Club VIP.",
      pitch: "Invite-only. Nightlife. Travel. Parties. Match with people at your event.",
      href: "/clubvip",
      cta: "Apply for access",
      img: "/assets/vip-portrait.webp",
      accent: "magenta",
      testId: "callout-user",
    },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-10" data-testid="audience-callouts" data-reveal="1">
      <div className="grid md:grid-cols-3 gap-4">
        {rows.map((r) => (
          <Link
            key={r.audience}
            to={r.href}
            data-testid={r.testId}
            className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] card-mag block"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={r.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-5">
              <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 text-${r.accent}`}>{r.audience}</div>
              <div className="font-fancy text-2xl md:text-3xl leading-tight text-white">{r.title}</div>
              <div className="text-xs text-white/75 mt-1.5">{r.pitch}</div>
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-white text-ink text-[10px] font-mono font-bold uppercase tracking-widest group-hover:bg-lime transition-colors">
                {r.cta} <ArrowUpRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeatureImageGrid() {
  // Iter 49 — straight-talk home: four image-icon cards for the core app pillars
  const items = [
    {
      href: "/ai-gallery",
      label: "AI Photo Gallery",
      sub: "Take a selfie — we find every photo of you from the night.",
      Icon: Camera,
      img: "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1200&auto=format&fit=crop&q=70",
      testId: "feat-ai-gallery",
    },
    {
      href: "/clubvip",
      label: "Match Room",
      sub: "Match with people going to the same event — before you walk in.",
      Icon: Users,
      img: "/assets/vip-portrait.webp",
      testId: "feat-match-room",
    },
    {
      href: "/vip",
      label: "VIP After Parties",
      sub: "Members-only tables, trips, and after-hours lists.",
      Icon: Crown,
      img: "/assets/festival-girls.webp",
      testId: "feat-vip",
    },
    {
      href: "/shop",
      label: "Shop",
      sub: "Event drops, DJ kits, creator merch.",
      Icon: ShoppingBag,
      img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&auto=format&fit=crop&q=70",
      testId: "feat-shop",
    },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="home-feature-grid">
      <div className="mb-6">
        <div className="chip mb-3">What you can do here</div>
        <h2 className="font-fancy text-2xl sm:text-3xl md:text-4xl text-white">
          Everything, <span className="marker-lime">one tap away.</span>
        </h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {items.map(({ href, label, sub, Icon, img, testId }) => (
          <Link key={href} to={href} data-testid={testId}
            className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] aspect-[4/5] card-mag block">
            <img src={img} alt={label} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent pointer-events-none" />
            <div className="absolute top-3 left-3 w-9 h-9 rounded-xl bg-lime text-ink flex items-center justify-center shadow-lg">
              <Icon className="w-4 h-4" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-4">
              <div className="font-fancy uppercase text-white text-xl leading-tight">{label}</div>
              <div className="text-[11px] text-white/80 mt-1 line-clamp-2">{sub}</div>
              <div className="inline-flex items-center gap-1 mt-2 text-[10px] font-mono uppercase tracking-widest text-lime group-hover:text-white">
                Open <ArrowUpRight className="w-3 h-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { city } = useCity();
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [cat, setCat] = useState("All");
  const [timeFilter, setTimeFilter] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState("event");
  const [cityFilter, setCityFilter] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [wlPhone, setWlPhone] = useState("");
  const [aiSet, setAiSet] = useState(false);
  const [favIds, setFavIds] = useState([]);
  const [galleryPreview, setGalleryPreview] = useState([]);
  const [vipPosts, setVipPosts] = useState([]);

  useEffect(() => {
    api.get("/events?limit=200").then(({ data }) => {
      const withGallery = (data || []).filter((e) => e.ai_photo_gallery_url || e.ai_gallery_qr_path).slice(0, 3);
      setGalleryPreview(withGallery);
    }).catch(() => {});
    if (user?.vip || user?.role === "admin") {
      api.get("/community/posts").then(({ data }) => setVipPosts((data || []).slice(0, 3))).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (city && !cityFilter) setCityFilter(city);
  }, [city]);

  const isPast = (e) => {
    if (!e?.date) return false;
    const endDate = e.end_date || e.date;
    const dateStr = e.end_time && /^\d{2}:\d{2}$/.test(e.end_time) ? `${endDate.split("T")[0]}T${e.end_time}:00` : endDate;
    const d = new Date(dateStr);
    if (!e.end_time) d.setHours(23, 59, 59, 999);
    return d < new Date();
  };

  useEffect(() => {
    api.get("/events")
      .then(({ data }) => setEvents(Array.isArray(data) ? data : []))
      .catch((err) => { console.error("Failed to fetch events:", err); toast.error("Couldn't load events. Please refresh."); setEvents([]); });
    if (user) api.get("/favorites/me").then(({ data }) => setFavIds(data.event_ids || [])).catch(() => {});
  }, [user]);

  const cities = React.useMemo(() => {
    const set = new Set();
    events.forEach((e) => e.city && set.add(e.city));
    return Array.from(set).sort();
  }, [events]);

  useEffect(() => {
    if (aiSet) return;
    let list = events;
    list = list.filter((e) => (typeFilter === "experience" ? e.type === "experience" : (!e.type || e.type === "event")));
    if (timeFilter === "upcoming") list = list.filter((e) => !isPast(e));
    else if (timeFilter === "past") list = list.filter(isPast);
    if (cat !== "All") list = list.filter((e) => e.category === cat);
    if (cityFilter) list = list.filter((e) => (e.city || "").toLowerCase() === cityFilter.toLowerCase());
    if (priceFilter !== "all") {
      list = list.filter((e) => {
        const prices = (e.tiers || []).map((t) => Number(t.price || 0));
        const min = prices.length ? Math.min(...prices) : 0;
        if (priceFilter === "free") return min === 0 || e.event_type === "free";
        if (priceFilter === "under-500") return min > 0 && min < 500;
        if (priceFilter === "500-2000") return min >= 500 && min <= 2000;
        if (priceFilter === "2000-plus") return min > 2000;
        return true;
      });
    }
    list = [...list].sort((a, b) => {
      const da = a?.date ? new Date(a.date).getTime() : 0;
      const db = b?.date ? new Date(b.date).getTime() : 0;
      return timeFilter === "past" ? db - da : da - db;
    });
    setFiltered(list);
  }, [cat, events, aiSet, timeFilter, typeFilter, cityFilter, priceFilter]);

  const isValidIndianMobile = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length === 10) return /^[6-9]\d{9}$/.test(digits);
    if (digits.length === 12 && digits.startsWith("91")) return /^91[6-9]\d{9}$/.test(digits);
    return false;
  };

  const joinWaitlist = async (module) => {
    if (!isValidIndianMobile(wlPhone)) return toast.error("Enter a valid Indian mobile (10 digits starting 6-9)");
    const digits = wlPhone.replace(/\D/g, "");
    const phone = digits.length === 10 ? `+91${digits}` : `+${digits}`;
    try { await api.post("/waitlist", { phone, module }); toast.success(`You're on the ${module} list.`); setWlPhone(""); }
    catch { toast.error("Could not join waitlist"); }
  };

  return (
    <Layout>
      {/* Live Match snippets ticker — FOMO strip pinned above the fold */}
      <MatchSnippetsTicker />
      {/* Latest events — auto-rotating hero strip above everything else */}
      <LatestEventsSlider events={events} />
      {/* Genie concierge — full width, top of homepage */}
      <GenieBar />
      {aiSet && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-2 mb-2">
          <button onClick={() => { setAiSet(false); setCat("All"); }} data-testid="ai-reset" className="text-xs font-mono uppercase tracking-widest text-muted underline">Reset — show all</button>
        </div>
      )}

      {/* HERO — mesh gradient, orbs, word-swap headline */}
      <Hero events={events} />

      {/* ITER 49 — WHAT YOU CAN DO HERE: 2×2 image-icon feature grid */}
      <FeatureImageGrid />

      {/* Feature tiles — Discover · AI · Pass · Match · Biz · Tribe */}
      <FeatureTiles />

      {/* Social proof — stats ribbon (SortMyScene style) */}
      <StatsRibbon />

      {/* FAN JOURNEY — the whole Eventom promise in 6 tiles */}
      <FanJourney />

      {/* TRENDING */}
      <TrendingSlider events={events} />

      {/* Category row — sharp editorial tags */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-8" data-testid="category-icon-row">
        <div className="chip mb-4">Browse by category</div>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" style={{ scrollbarWidth: "none" }}>
          {HOMEPAGE_CATEGORIES.map((c) => (
            <button key={c.key} onClick={() => { setCat(c.key); document.getElementById("events-grid")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid={`cat-icon-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
              className="shrink-0 inline-flex items-center gap-2 border border-line bg-surface px-4 py-2.5 text-[11px] font-mono font-bold uppercase tracking-widest text-text/85 hover:border-magenta hover:text-text transition-colors">
              <c.icon className="w-4 h-4 text-muted" />
              {c.key}
            </button>
          ))}
        </div>
      </section>

      {/* EVENTS GRID */}
      <section id="events-grid" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="chip mb-3">The circuit</div>
            <h2 className="font-fancy text-2xl sm:text-3xl md:text-4xl text-white">
              All the {typeFilter === "experience" ? "experiences" : "events"}. <span className="text-magenta">All at once.</span>
            </h2>
          </div>
          <div className="inline-flex gap-1 p-1 rounded-full glass !rounded-full" data-testid="time-filter">
            {[["upcoming","Upcoming"],["past","Past"],["all","All"]].map(([k,l]) => (
              <button key={k} data-testid={`time-${k}`} onClick={() => { setAiSet(false); setTimeFilter(k); }}
                className={`px-4 py-1.5 text-xs font-mono font-bold uppercase tracking-widest rounded-full transition-colors ${timeFilter === k ? "bg-lime text-ink" : "text-muted hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="inline-flex gap-1 p-1 rounded-full bg-surface mb-4" data-testid="type-filter">
          {[["event","Events"],["experience","Experiences"]].map(([k,l]) => (
            <button key={k} data-testid={`type-${k}`} onClick={() => { setAiSet(false); setTypeFilter(k); }}
              className={`px-5 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full transition-colors ${typeFilter === k ? "bg-pink text-white" : "text-muted hover:text-white"}`}>{l}</button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map((c) => (
            <button key={c} data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => { setAiSet(false); setCat(c); }}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full transition-colors ${cat === c && !aiSet ? "bg-pink text-white" : "bg-surface text-muted hover:text-white"}`}>{c}</button>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-8 items-center">
          <select data-testid="city-filter" value={cityFilter} onChange={(e) => { setAiSet(false); setCityFilter(e.target.value); }} className="input-dark !py-2 !px-3 !text-xs !w-auto">
            <option value="">All cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="inline-flex gap-1 p-1 rounded-full bg-surface" data-testid="price-filter">
            {[["all","All"],["free","Free"],["under-500","< ₹500"],["500-2000","₹500-2K"],["2000-plus","₹2K+"]].map(([k,l]) => (
              <button key={k} data-testid={`price-${k}`} onClick={() => { setAiSet(false); setPriceFilter(k); }}
                className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest rounded-full transition-colors ${priceFilter === k ? "bg-lime text-ink" : "text-muted hover:text-white"}`}>{l}</button>
            ))}
          </div>
          {(cityFilter || priceFilter !== "all" || cat !== "All") && (
            <button data-testid="clear-filters" onClick={() => { setCityFilter(""); setPriceFilter("all"); setCat("All"); }} className="text-[11px] font-mono uppercase tracking-widest text-muted underline hover:text-lime">Clear filters</button>
          )}
        </div>
        {filtered.length === 0 ? (
          <div className="glass rounded-3xl p-12 text-center font-display text-2xl">No events found. Try another vibe.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((e, i) => <EventCard key={e.id} event={e} index={i} isPast={isPast(e)} favorited={favIds.includes(e.id)} onToggleFav={(id, on) => setFavIds((prev) => on ? [...prev, id] : prev.filter((x) => x !== id))} />)}
          </div>
        )}
      </section>

      {/* AI PHOTOS — coral/violet gradient bleed */}
      <section className="section-pink py-16 relative grain" data-testid="ai-gallery-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="chip !border-magenta/50 !text-magenta mb-3"><Camera className="w-3 h-3" /> Eventom AI Camera</div>
            <h2 className="font-fancy text-2xl md:text-4xl lg:text-5xl text-white">Were you there?<br /><span className="text-lime">Find your photos.</span></h2>
            <p className="mt-4 text-white/80 max-w-md text-lg">Take a selfie. Our AI pulls every photo of you from the event — in seconds.</p>
            <Link to="/ai-gallery" data-testid="ai-photos-cta" className="mt-6 btn-lime"><Camera className="w-4 h-4" /> Access AI Gallery</Link>
          </div>
          <div className="grid grid-cols-3 gap-3" data-testid="ai-photos-latest-grid">
            {(() => {
              const topSix = (events || [])
                .filter((e) => (e.ai_photo_gallery_url || e.external_gallery_url || e.ai_gallery_qr_path) && e.banner)
                .slice(0, 6);
              if (topSix.length === 0) {
                const fallback = [
                  // Premium female lifestyle + editorial crowd shots (curated Unsplash)
                  "https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=600&auto=format&fit=crop&q=70", // women toast
                  "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=600&auto=format&fit=crop&q=70", // festival portrait
                  "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=600&auto=format&fit=crop&q=70", // nightlife crowd
                  "https://images.unsplash.com/photo-1583912086096-8c60d75a53f9?w=600&auto=format&fit=crop&q=70", // rooftop women
                  "https://images.unsplash.com/photo-1493225458393-427ce50e0efc?w=600&auto=format&fit=crop&q=70", // music festival
                  "https://images.unsplash.com/photo-1571266028243-e4bb35f0af79?w=600&auto=format&fit=crop&q=70", // party crowd
                ];
                return fallback.map((u, i) => (
                  <div key={i} className={`polaroid !p-2 !pb-4 ${i % 2 === 0 ? "-rotate-2" : "rotate-2"}`}>
                    <div className="aspect-square overflow-hidden"><img src={u} alt="" className="w-full h-full object-cover" /></div>
                  </div>
                ));
              }
              return topSix.map((e, i) => (
                <Link key={e.id} to={e.slug ? `/e/${e.slug}` : `/events/${e.id}`} data-testid={`ai-photo-card-${i}`}
                  className={`polaroid !p-2 !pb-3 block ${i % 2 === 0 ? "-rotate-2" : "rotate-2"} card-lift`}>
                  <div className="aspect-square overflow-hidden">
                    <img src={e.banner} alt={e.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="font-script text-xs mt-1 text-white/70 truncate">{e.title}</div>
                </Link>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* VIP preview — violet */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="vip-updates-preview">
        <div className="card-purple p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <div className="chip !border-white/25 !text-white/80 mb-2"><Crown className="w-3 h-3" /> Eventom Club VIP</div>
              <h2 className="font-display text-2xl md:text-4xl leading-tight text-white">All-access, flat <span className="text-lime">₹99.</span></h2>
              <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest text-white/60">
                {["Community", "After Parties", "Matchmaking", "Early Access", "Meet & Greet"].map((b) => <span key={b} className="px-2 py-0.5 rounded-full border border-white/15">{b}</span>)}
              </div>
            </div>
            <Link to="/vip" data-testid="join-vip-cta" className="btn-purple"><Crown className="w-4 h-4" /> Join VIP</Link>
          </div>
          {vipPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-3">
              {vipPosts.map((p) => (
                <div key={p.id} className="glass !rounded-2xl p-4" data-testid={`vip-preview-post-${p.id}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-lime mb-1">{p.user_name}</div>
                  <div className="text-sm text-white/85 line-clamp-3">{p.text}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-white/60 font-mono">VIP-only feed with insider drops, matchmaking previews & exclusive party invites. Apply to unlock.</div>
          )}
        </div>
      </section>

      {/* Squad Leaderboard teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4" data-testid="leaderboard-teaser">
        <LeaderboardTeaser />
      </section>

      {/* Organisers + Brands */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-2 gap-6" data-testid="for-organisers-section">
        <div className="card-lime p-7 md:p-9">
          <div className="chip !border-lime/50 !text-lime mb-3">For Organisers</div>
          <h2 className="font-display text-2xl md:text-4xl leading-tight mb-2 text-white">Start free. <span className="text-lime">Scale with AI.</span></h2>
          <p className="text-white/70 mb-5 text-sm">Ship tickets, scan QRs, dispatch invites — all from one dashboard.</p>
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3" data-testid="org-cap-1"><TrendingUp className="w-5 h-5 text-lime mt-0.5" /><div><div className="font-heading font-bold text-sm">Reach The Right Fans</div><div className="text-xs text-white/55">AI-matched discovery + city-first feed.</div></div></div>
            <div className="flex items-start gap-3" data-testid="org-cap-2"><ScanLine className="w-5 h-5 text-skyx mt-0.5" /><div><div className="font-heading font-bold text-sm">One-Tap Check-In</div><div className="text-xs text-white/55">QR scanner + multi-admission logic built in.</div></div></div>
            <div className="flex items-start gap-3" data-testid="org-cap-3"><Zap className="w-5 h-5 text-magenta mt-0.5" /><div><div className="font-heading font-bold text-sm">Fast Payouts</div><div className="text-xs text-white/55">3.6% flat — no lock-in, direct to your bank.</div></div></div>
          </div>
          <Link to="/organisers" data-testid="cta-organisers" className="btn-primary inline-flex">List Your Event <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
        <div className="card-pink p-7 md:p-9" data-testid="for-brands-section">
          <div className="chip !border-magenta/50 !text-magenta mb-3">For Brands</div>
          <h2 className="font-display text-2xl md:text-4xl leading-tight mb-2 text-white">Real crowds. <span className="text-magenta">Real ROI.</span></h2>
          <p className="text-white/70 mb-5 text-sm">Sponsor concerts, house parties, festivals. Audience data + on-ground activation.</p>
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3" data-testid="brand-cap-1"><Users className="w-5 h-5 text-purple mt-0.5" /><div><div className="font-heading font-bold text-sm">Gen-Z Audiences</div><div className="text-xs text-white/55">Live-entertainment fans across 12 Indian cities.</div></div></div>
            <div className="flex items-start gap-3" data-testid="brand-cap-2"><Building2 className="w-5 h-5 text-magenta mt-0.5" /><div><div className="font-heading font-bold text-sm">Full-Stack Activation</div><div className="text-xs text-white/55">Content, sampling, branded VIP rooms — end to end.</div></div></div>
            <div className="flex items-start gap-3" data-testid="brand-cap-3"><Sparkles className="w-5 h-5 text-skyx mt-0.5" /><div><div className="font-heading font-bold text-sm">AI-Powered Match</div><div className="text-xs text-white/55">Right event, right city, right fan segment.</div></div></div>
          </div>
          <Link to="/brands-portal" data-testid="cta-brands" className="btn-pink inline-flex">Partner With Eventom <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* HOW IT WORKS — SortMyScene-style 3-step for first-time visitors */}
      <HowItWorks />

      {/* REFERRAL LEADERBOARD — growth loop, competitive */}
      <ReferralLeaderboard />

      {/* MARQUEE */}
      <section className="py-6">
        <div className="marquee-container py-3">
          <div className="marquee-track font-display text-3xl md:text-5xl">
            {Array(4).fill("EVENTOM · EVERY EVENT · EVERY CROWD · ONE PLATFORM · ").map((t, i) => (<span key={i} className="whitespace-nowrap">{t}</span>))}
          </div>
        </div>
      </section>

      {/* Onboarding — invite 2 friends unlock free ticket (shows once after city pick) */}
      <OnboardingInviteNudge />
    </Layout>
  );
}
